// eslint-disable-next-line no-restricted-imports
import {
  lightTheme as libraryTheme,
  darkTheme as libraryDarkTheme,
  PaletteColor,
} from "@brexhq/metal-foundation";

import { hydrateFontFamiliesInTheme } from "@brexhq/shared/utils/themeHydration";

import breakpoints from "./breakpoints";
import { primaryFonts, FontWeight } from "./fonts";
import grid from "./grid";
import gridRebirth from "./grid-rebirth";
import radius from "./radius";
import spacing from "./spacing";
import transitions from "./transitions";
import zIndex from "./z-index";

// Legacy theme colors
// PaletteColor is a string hex code, so casting is safe
export const LegacyThemeColors = {
  PAPER_BACKGROUND: "#F5F0F0" as PaletteColor,
  TRUST_GREEN: "#053C0B" as PaletteColor,
  TRUST_MAROON: "#530022" as PaletteColor,
  TRUST_BLUE: "#04233A" as PaletteColor,
  TRUST_PURPLE: "#330A4A" as PaletteColor,
  ENERGY_YELLOW: "#EED600" as PaletteColor,
  ENERGY_BLUE: "#65DDE4" as PaletteColor,
  ENERGY_ORANGE: "#FF7028" as PaletteColor,
  ENERGY_GREEN: "#76E488" as PaletteColor,
} satisfies Record<string, PaletteColor>;

/** (Kristin - Nov 7 2022)
 * We're using a single theme and can't have objects with the same keys that
 * overwrite theme values that could be needed by the component library. We
 * should try and standardize on the libraryTheme values wherever we can, but in
 * some cases it makes sense to have different values.
 */
const baseTheme = {
  fonts: { primaryFonts, FontWeight },
  zIndex: {
    ...libraryTheme.zIndex,
    ...zIndex,
  },
  grid,
  breakpoints: {
    ...libraryTheme.breakpoints,
    ...breakpoints,
  },
  spacing: {
    ...libraryTheme.spacing,
    ...spacing,
  },
  transitions,
  radius,
};

// Hydrate the Metal Foundation theme to use our CSS variables
const hydratedLightTheme = hydrateFontFamiliesInTheme(libraryTheme);

export const defaultTheme = {
  ...hydratedLightTheme,
  ...baseTheme,

  //TODO- Deprecate rebirth theme object, remove library colors (and any unused) from that theme
  rebirth: {
    fonts: {
      family: "var(--font-inter), sans-serif",
      FontWeight: FontWeight,
    },
    grid: gridRebirth,
  },
  maxWidths: {
    moduleGrid: "1680px",
    textContent: "760px",
    allContent: 2500,
  },
  navHeights: {
    desktop: 60,
    mobile: 61,
  },
  maxHeights: {
    fullHeight: 1200,
  },
  productLogSizes: {
    searchBoxHeight: 68,
    heroMobileHieght: 240,
    heroDesktopHeight: 280,
    maxWidth: 1499,
  },
};

export type DefaultTheme = typeof defaultTheme;

export const allBorderRadii = Object.keys(
  defaultTheme["radius"],
) as Array<BorderRadii>;
export type BorderRadii = keyof typeof defaultTheme["radius"];

const getPaletteColorKeys = (obj, prefix = "") =>
  Object.keys(obj).reduce((res: any[], el) => {
    if (typeof obj[el] === "object" && obj[el] !== null) {
      return [...res, ...getPaletteColorKeys(obj[el], prefix + el + ".")];
    }
    return [...res, prefix + el];
  }, []);

const allColors = defaultTheme.colors;

/** (kristin - Nov 6 2022)
 * @deprecated Do not use - Reference colors from theme and useTheme only.
 * When we use the hard coded string values, it makes it very difficult to
 * make changes to theme colors and easily find/replace
 */
export const allColorKeys = getPaletteColorKeys(allColors);
export const darkTheme = { ...defaultTheme, colors: libraryDarkTheme.colors };
